import React from "react";
import {
  Dialog,
  Box,
  Avatar,
  Typography,
  Button,
  styled,
  keyframes,
} from "@mui/material";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { ChatSession } from "../types/chat";

interface ModalVideoCallingProps {
  open: boolean;
  session: ChatSession | null;
  onCancel: () => void;
}

// Animação de pulsação
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: "40px",
    minWidth: "400px",
    maxWidth: "500px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
  },
}));

const AvatarWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginBottom: "24px",
  "& .MuiAvatar-root": {
    width: "120px",
    height: "120px",
    border: "4px solid rgba(255, 255, 255, 0.3)",
    animation: `${pulse} 2s ease-in-out infinite`,
  },
});

const CancelButton = styled(Button)({
  marginTop: "32px",
  padding: "12px 32px",
  borderRadius: "50px",
  backgroundColor: "#f44336",
  color: "#fff",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#d32f2f",
  },
});

const ModalVideoCalling: React.FC<ModalVideoCallingProps> = ({
  open,
  session,
  onCancel,
}) => {
  const getSessionDisplayName = () => {
    if (!session) return "Usuário";
    
    if (session.session_type === "external") {
      return session.candidate_name || session.phone_number || "Contato Externo";
    }
    
    if (session.session_name) {
      return session.session_name;
    }
    
    if (session.participants && session.participants.length > 0) {
      const otherParticipant = session.participants[0];
      return otherParticipant.user?.nomecomp || otherParticipant.user?.nome || "Usuário";
    }
    
    return "Usuário";
  };

  const getSessionAvatar = () => {
    if (!session) return undefined;
    
    if (session.session_type === "external") {
      return session.candidate_avatar || session.session_avatar;
    }
    
    if (session.session_avatar) {
      return session.session_avatar;
    }
    
    if (session.participants && session.participants.length > 0) {
      return session.participants[0].user?.img_usuario;
    }
    
    return undefined;
  };

  return (
    <StyledDialog open={open} maxWidth="sm" fullWidth>
      <Box textAlign="center">
        <AvatarWrapper>
          <Avatar
            src={getSessionAvatar()}
            alt={getSessionDisplayName()}
            sx={{
              bgcolor: "#7b1fa2",
            }}
          >
            {getSessionDisplayName().charAt(0).toUpperCase()}
          </Avatar>
        </AvatarWrapper>

        <Typography variant="h5" fontWeight={600} gutterBottom>
          {getSessionDisplayName()}
        </Typography>

        <Typography
          variant="body1"
          sx={{ opacity: 0.9, marginTop: "8px" }}
        >
          Chamando...
        </Typography>

        <Box
          sx={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              animation: `${pulse} 1.5s ease-in-out infinite`,
              animationDelay: "0s",
            }}
          />
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              animation: `${pulse} 1.5s ease-in-out infinite`,
              animationDelay: "0.3s",
            }}
          />
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              animation: `${pulse} 1.5s ease-in-out infinite`,
              animationDelay: "0.6s",
            }}
          />
        </Box>

        <CancelButton
          onClick={onCancel}
          startIcon={<PhoneDisabledIcon />}
          fullWidth
        >
          Cancelar chamada
        </CancelButton>
      </Box>
    </StyledDialog>
  );
};

export default ModalVideoCalling;
